import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useOrderLabInElation } from '../state/data/lab';
import { CKM_BASELINE_LAB_PANEL, CONDITION_TO_ICD10 } from '../config/medical-constants';

// One-click lab ordering via Elation (WF31).
export default function LabOrderModal({ task, patient, carePlan, currentUser, onClose, onSuccess }) {
  const orderMut = useOrderLabInElation();
  const submitting = orderMut.isPending;
  const [error, setError] = useState(null);

  // Selected tests — default to the full CKM baseline panel.
  const [selectedLoincs, setSelectedLoincs] = useState(
    () => new Set(CKM_BASELINE_LAB_PANEL.map(t => t.loinc))
  );

  // ICD-10 codes derived from the patient's conditions.
  const initialIcd10 = useMemo(() => {
    const conditions = carePlan?.conditions || carePlan?.qualifying_conditions || [];
    return Array.from(new Set(
      conditions.map(c => CONDITION_TO_ICD10[c] || CONDITION_TO_ICD10[c?.trim?.()]).filter(Boolean)
    ));
  }, [carePlan]);
  const [icd10Input, setIcd10Input] = useState(initialIcd10.join(', '));

  const [priority, setPriority] = useState('routine');
  const [labVendor, setLabVendor] = useState('quest');
  const [notes, setNotes] = useState('');

  function toggleTest(loinc) {
    setSelectedLoincs(prev => {
      const next = new Set(prev);
      if (next.has(loinc)) next.delete(loinc); else next.add(loinc);
      return next;
    });
  }

  async function handleSubmit() {
    const tests = CKM_BASELINE_LAB_PANEL.filter(t => selectedLoincs.has(t.loinc));
    if (tests.length === 0) {
      setError('Select at least one test.');
      return;
    }
    const icd10Codes = icd10Input
      .split(/[,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);

    setError(null);
    orderMut.mutate(
      {
        withingsUserId: patient.userid,
        taskId: task.task_id,
        tests,
        icd10Codes,
        priority,
        labVendor,
        notes: notes.trim() || undefined,
        orderedByRole: currentUser?.role || 'clinician',
        orderedByName: currentUser
          ? [currentUser.firstname, currentUser.lastname].filter(Boolean).join(' ') || undefined
          : undefined,
        orderedByUserId: currentUser?.accountid ? String(currentUser.accountid) : undefined,
      },
      {
        onSuccess: (result) => onSuccess?.(result),
        onError: (e) => setError(e.message || 'Lab order failed'),
      },
    );
  }

  return createPortal((
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--wi-background-primary)', borderRadius: 12,
          width: 560, maxWidth: '92vw', maxHeight: '88vh', overflow: 'auto',
          padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h3 className="h4" style={{ margin: 0 }}>Order labs in Elation</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--wi-text-secondary)' }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="detail1 text-secondary" style={{ marginTop: 0, marginBottom: 16 }}>
          {task.title} · {patient.first_name || ''} {patient.last_name || ''}
        </p>

        <label className="eyebrow text-secondary" style={{ display: 'block', marginBottom: 6 }}>Tests</label>
        <div style={{ border: '1px solid var(--wi-border-primary)', borderRadius: 8, padding: 8, marginBottom: 16 }}>
          {CKM_BASELINE_LAB_PANEL.map(t => (
            <label key={t.loinc} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px' }}>
              <input
                type="checkbox"
                checked={selectedLoincs.has(t.loinc)}
                onChange={() => toggleTest(t.loinc)}
              />
              <span className="body2" style={{ flex: 1 }}>{t.display}</span>
              <span className="detail1 text-secondary" style={{ fontFamily: 'monospace' }}>{t.loinc}</span>
            </label>
          ))}
        </div>

        <label className="eyebrow text-secondary" style={{ display: 'block', marginBottom: 6 }}>
          ICD-10 diagnoses (comma-separated)
        </label>
        <input
          type="text"
          value={icd10Input}
          onChange={e => setIcd10Input(e.target.value)}
          placeholder="e.g., I10, E11.9"
          style={{
            width: '100%', padding: '8px 10px', border: '1px solid var(--wi-border-primary)',
            borderRadius: 6, marginBottom: 16, fontFamily: 'var(--wi-font-family)',
          }}
        />

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label className="eyebrow text-secondary" style={{ display: 'block', marginBottom: 6 }}>Priority</label>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--wi-border-primary)', borderRadius: 6 }}
            >
              <option value="routine">Routine</option>
              <option value="stat">STAT</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="eyebrow text-secondary" style={{ display: 'block', marginBottom: 6 }}>Lab vendor</label>
            <select
              value={labVendor}
              onChange={e => setLabVendor(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--wi-border-primary)', borderRadius: 6 }}
            >
              <option value="quest">Quest</option>
              <option value="labcorp">LabCorp</option>
              <option value="in_house">In-house</option>
            </select>
          </div>
        </div>

        <label className="eyebrow text-secondary" style={{ display: 'block', marginBottom: 6 }}>Notes to lab (optional)</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Fasting instructions, special handling, etc."
          style={{
            width: '100%', padding: '8px 10px', border: '1px solid var(--wi-border-primary)',
            borderRadius: 6, marginBottom: 16, fontFamily: 'var(--wi-font-family)', resize: 'vertical',
          }}
        />

        {error && (
          <div style={{
            background: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)',
            padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: 'none', border: '1px solid var(--wi-border-primary)', borderRadius: 6,
              padding: '8px 16px', cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--wi-font-family)', fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selectedLoincs.size === 0}
            style={{
              background: 'var(--wi-action-primary)', color: 'var(--wi-on-action-primary)', border: 'none', borderRadius: 6,
              padding: '8px 16px', cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--wi-font-family)', fontWeight: 500,
              opacity: submitting || selectedLoincs.size === 0 ? 0.6 : 1,
            }}
          >
            {submitting ? 'Submitting to Elation…' : `Order ${selectedLoincs.size} test${selectedLoincs.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
